<template>
  <div>
    <!-- Add Review Meeting -->
    <add-review-meeting-date
    :oldReviewDate="oldReviewDate"
    :oldActivatingDate="oldActivatingDate"
    :campaignID="campaignID"
    :options="{theme: 'default'}"
    @updated="invokeReviewMeetingUpdated"
    @reloadTimeline="getCampaignTimelines"/>
    <!-- edit mode -->
    <v-row class="pb-2 text-right">
      <v-col class="text-left" cols="12" xs="6" md="6" justify="left" align="left" pt-0>
        <v-btn class="primary" small @click="downloadImage()">
          <v-icon v-on="on" small>download</v-icon>
          Download Image
        </v-btn>
      </v-col>
      <v-col class="text-right" cols="12" xs="6" md="6" justify="right" align="right" pt-0>
        <v-btn class="primary" small @click="changeMode('edit')" v-if="currentMode === 'view'">
          <v-icon v-on="on" small>edit</v-icon>
          Edit Mode
        </v-btn>
        <v-btn color="error" small @click="changeMode('view')" v-if="currentMode === 'edit'">
          <v-icon v-on="on" small>close</v-icon>
          Cancel
        </v-btn>
        <v-btn color="success" small :disabled="disableSave" @click="saveChanges()" v-if="currentMode === 'edit'">
          <v-icon v-on="on" small>check</v-icon>
          Save Changes
        </v-btn>
      </v-col>
    </v-row>
    <div :class="`text-center print-table-inner ${currentMode === 'view' ? 'table-view-mode' : 'table-edit-mode'}`">
      <div id="img-node">
        <div style="padding:5px">
          <!-- BEGIN vue data table -->
          <v-data-table
          dense
          :headers="computedHeaders" 
          :search="filter" 
          :items="filteredItems" 
          :key="dataTableKey"
          hide-default-footer
          class="campaign-timeline-table elevation-1">
            <template v-slot:body="props">
                <draggable
                :list="props.items"
                tag="tbody"
                handle=".move-cursor"
                @update="handleDataTableDragDrop(props.items)" >
                  <tr
                    v-for="(item, parentIndex) in props.items"
                    :key="parentIndex"
                  >
                    <td class="text-start" :style="`width:${viewMode ? '20%' : '40%'}`">
                      <div class="d-flex flex-row justify-left pt-4">
                        <v-icon
                          small
                          class="move-cursor"
                          v-if="editMode"
                        >
                          mdi-arrow-all
                        </v-icon>
                        <div>
                          <p class="text-center mb-0" v-if="viewMode">
                            {{item.startDate}}
                          </p>
                          <div class="d-flex flex-row">
                            <v-text-field
                              class="edit-date-box"
                              placeholder="Start Date"
                              v-model="item.startDate"
                              v-if="editMode"
                              outlined
                              dense
                              hide-details
                              @input="checkDate(item, 'startDate', 'validStartDate')"
                              @keyup.enter="checkDate(item, 'startDate', 'validStartDate')"
                            >
                            </v-text-field>
                            <div class="date-picker-inner">
                              <date-picker
                                className="input-field"
                                :options="{theme: 'icon'}"
                                :status="item.startDateCal"
                                @changeDate="setDateFromPicker(item, 'startDate', ...arguments)"
                                v-if="editMode"
                              />
                            </div>
                            &nbsp;<span :class="`pt-2 ${utils.func.getDaysDiff(item.startDate) < 0 ? 'text-red': ''}`" v-if="editMode && item.startDate">({{utils.func.getDaysDiff(item.startDate)}})</span>
                          </div>
                          <span class="text-danger ma-0 pt-1" v-if="editMode && !item.validStartDate">Invalid Start Date</span>
                        </div>
                        <div :class="`px-2 ${editMode ? 'pt-2' : ''} text-center`" v-if="(item.startDate && item.endDate) || editMode">-</div>
                        <div>
                          <p class="text-center mb-0" v-if="viewMode">
                            {{item.endDate}}
                          </p>
                          <div class="d-flex flex-row">
                            <v-text-field
                              class="edit-date-box"
                              placeholder="End Date"
                              v-model="item.endDate"
                              v-if="editMode"
                              outlined
                              dense
                              hide-details
                              @input="checkDate(item, 'endDate', 'validEndDate')"
                              @keyup.enter="checkDate(item, 'endDate', 'validEndDate')"
                            >
                            </v-text-field>
                            <div class="date-picker-inner">
                              <date-picker
                                className="input-field"
                                :options="{theme: 'icon'}"
                                :status="item.endDateCal"
                                @changeDate="setDateFromPicker(item, 'endDate', ...arguments)"
                                v-if="editMode"
                              />
                            </div>
                            &nbsp;<span :class="`pt-2 ${utils.func.getDaysDiff(item.endDate) < 0 ? 'text-red': ''}`" v-if="editMode && item.endDate">({{utils.func.getDaysDiff(item.endDate)}})</span>
                          </div>
                          <span class="text-danger ma-0 pt-1" v-if="editMode && !item.validEndDate">Invalid End Date</span>
                        </div>
                      </div>
                      <div class="text-danger text-left ma-0 pt-1" v-if="editMode && !item.startDate && !item.endDate && item.actionType !== timelineTypes.EMPTY">Please enter start date or end date</div>
                      <!-- activating due date section -->
                      <div v-if="showActivatingDueDateBox(item)" class="pb-3">
                        <div v-if="editMode">
                          <hr class="mb-2">
                          <div class="d-flex flex-row pt-3 pb-3">
                            <span v-if="editMode">Enter Date for IMS to remind you on brief due date:</span>
                            <div class="d-flex flex-row" v-if="editMode">
                              <v-text-field
                                placeholder=""
                                v-model="item.activatingDueDate"
                                outlined
                                dense
                                hide-details
                                @input="checkDate(item, 'activatingDueDate', 'validActivatingDueDate')"
                                @keyup.enter="checkDate(item, 'activatingDueDate', 'validActivatingDueDate')"
                              >
                              </v-text-field>
                              <div class="date-picker-inner">
                                <date-picker
                                  className="input-field"
                                  :options="{theme: 'icon'}"
                                  :status="item.activatingDueDateCal"
                                  @changeDate="setDateFromPicker(item, 'activatingDueDate', ...arguments)"
                                />
                              </div>
                            </div>
                          </div>
                          <span class="text-danger ma-0 pt-1" v-if="editMode && !item.validActivatingDueDate">Invalid Activating Due Date</span>
                        </div>
                        <div v-if="viewMode && !hideForPrint" style="margin-top:-15px">
                          <p class="text-center mb-0">
                            Brief Due Date: {{item.activatingDueDate}}
                          </p>
                        </div>
                      </div>
                      <!-- end of activating due date section -->
                      
                      <!-- pairing due date section -->
                      <div v-if="showPairingDueDateBox(item)" class="pb-3">
                        <div v-if="editMode">
                          <hr class="mb-2">
                          <div class="d-flex flex-row pt-3 pb-3">
                            <span v-if="editMode">Enter Date to prompt CC on lineup due date:</span>
                            <div class="d-flex flex-row" v-if="editMode">
                              <v-text-field
                                placeholder=""
                                v-model="item.pairingDueDate"
                                outlined
                                dense
                                hide-details
                                @input="checkDate(item, 'pairingDueDate', 'validPairingDueDate')"
                                @keyup.enter="checkDate(item, 'pairingDueDate', 'validPairingDueDate')"
                              >
                              </v-text-field>
                              <div class="date-picker-inner">
                                <date-picker
                                  className="input-field"
                                  :options="{theme: 'icon'}"
                                  :status="item.pairingDueDateCal"
                                  @changeDate="setDateFromPicker(item, 'pairingDueDate', ...arguments)"
                                />
                              </div>
                            </div>
                          </div>
                          <span class="text-danger ma-0 pt-1" v-if="editMode && !item.validPairingDueDate">Invalid Pairing Due Date</span>
                        </div>
                        <div v-if="viewMode && !hideForPrint" style="margin-top:-15px">
                          <p class="text-center mb-0">
                            Lineup Due Date: {{item.pairingDueDate}}
                          </p>
                        </div>
                      </div>
                      <!-- end of activating due date section -->
                    </td>
                    <td class="text-start" :style="`width:${viewMode ? '45%' : '40%'}`">
                      <h4 :class="`${item.actionType && item.actionType.value ? 'font-weight-bold' : ''} mb-1 mt-1`" style="font-size:16px" v-if="viewMode">
                        {{displayActionType(item.actionType)}}
                      </h4>
                      <v-combobox
                        label=""
                        class="mt-1 mb-1"
                        style="width:200px"
                        v-model="item.actionType"
                        :items="timelineTypeLists"
                        item-text="title"
                        item-value="value"
                        dense
                        outlined
                        hide-details
                        v-if="editMode"
                        @change="changeActionType()"
                      >
                      </v-combobox>
                      <p class="mb-1" v-html="formatDesc(item.actionDesc)" v-if="viewMode"></p>
                      <v-textarea
                        class="mb-1"
                        v-model="item.actionDesc"
                        outlined
                        dense
                        rows="2"
                        hide-details
                        v-if="editMode"
                      >
                      </v-textarea>
                    </td>
                    <td class="text-start" :style="`width:${viewMode ? '25%' : '30%'}`">
                      <p v-if="viewMode">{{item.actionBy}}</p>
                      <v-text-field
                        class="edit-date-box"
                        v-model="item.actionBy"
                        v-if="editMode"
                        outlined
                        dense
                        hide-details
                      >
                      </v-text-field>
                    </td>
                    <td class="text-start" style="width:5%" v-if="editMode">
                      <v-btn small icon color="red" @click="deleteItem(item)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </draggable>
            </template>
            <template v-slot:no-data>No Data</template>
          </v-data-table>
        </div>
      </div>
      <v-row class="mt-1" v-if="currentMode === 'edit'">
        <v-col class="text-left" cols="12" xs="12" md="12" justify="left" align="left" pt-0>
          <v-btn class="primary" @click="addAction">
            <v-icon v-on="on">add</v-icon> Add Action
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <!-- Linear Progress Loading Dialog -->
    <linear-progress :loading="isLoading" text="Generating..." />
  </div>
</template>
<script>
// import campaignHelper from '../campaignHelper';
import moment from 'moment';
import { mapState, mapGetters } from 'vuex'
import timelineConfig from './config'
// import orderBy from 'lodash/orderBy';
import * as htmlToImage from 'html-to-image';
import utils from '@/utils'
import draggable from 'vuedraggable'
import DatePicker from '@/components/scoped/custom/DatePicker'
import LinearProgress from '@/components/scoped/custom/LinearProgress';
import AddReviewMeetingDate from './AddReviewMeetingDate'
// import { toPng, toJpeg, toBlob, toPixelData, toSvg } from 'html-to-image';
export default {
  name: 'campaign-timeline-datatable',
  components: {
    DatePicker,
    LinearProgress,
    AddReviewMeetingDate,
    draggable
  },
  props: [
    'campaignID',
    'selectedCampaign',
    'refresh'
  ],
  data() {
    return {
      headers: timelineConfig.tableHeaders,
      timelineTypeLists: timelineConfig.timelineTypeLists,
      timelineTypes: timelineConfig.timelineTypes,
      filteredItems: [],
      filter: '',
      oldReviewDate: '',
      oldActivatingDate: '',
      currentMode: 'view',
      isLoading: false,
      disableSave: false,
      hideForPrint: false,
      utils
    }
  },
  computed: {
    ...mapState({
      detail: state => state.campaignTimeline.timelineDetail,
      reviewMeetingDetail: state => state.campaignTimeline.reviewMeetingDetail,
      savedTimeline: state => state.campaignTimeline.savedTimeline
    }),
    ...mapGetters('campaignTimeline', [
      'getStageDays'
    ]),
    viewMode() {
      return this.currentMode === 'view'
    },
    editMode() {
      return this.currentMode === 'edit'
    },
    computedHeaders() {
      if (this.viewMode) {
        return this.headers.filter(h => h.value !== 'controlAction')
      }
      return this.headers
    }
  },
  watch: {
    refresh(value) {
      if (value) this.getCampaignTimelines()
    }
  },
  mounted() {
  },
  methods: {
    async getCampaignTimelines() {
      this.filteredItems = []
      this.oldReviewDate = ''
      this.oldActivatingDate = ''
      const timelineDetail = await this.$store.dispatch('campaignTimeline/getCampaignTimeline', this.campaignID)
      if (timelineDetail && Array.isArray(timelineDetail.schedules) && timelineDetail.schedules.length) {
        this.filteredItems = timelineDetail.schedules.map(schedule => Object.assign({}, schedule))
        this.beautifyScheduleData()
        this.filteredItems = this.resetDateValidators(this.filteredItems)
        if (timelineDetail.activatingDate) {
          this.oldActivatingDate = timelineDetail.activatingDate
        }
      } else {
        this.populateDefaultItems()
      }
      await this.$store.dispatch('campaignTimeline/getReviewMeeting', this.campaignID)
      if (this.reviewMeetingDetail.reviewMeetingDate) { // set old meeting review date value
        this.oldReviewDate = this.reviewMeetingDetail.reviewMeetingDate ? this.reviewMeetingDetail.reviewMeetingDate : ''
      }
    },
    beautifyScheduleData() {
      this.filteredItems = this.filteredItems.map(f => {
        let matchType = this.timelineTypeLists.find(t => t.value === f.actionType)
        if (matchType) {
          f.actionType = matchType
        }
        if (f.actionType === 0) f.actionType = '-'
        return f
      })
    },
    changeMode(mode) {
      this.currentMode = mode
      if (mode === 'edit') {
        if (!this.detail.activatingDate) {
          // if no act date, alert to enter it
          this.$swal({
            text: 'Please Enter Activating Date.',
            type: 'warning'
          })
          this.currentMode = 'view'
        }
        this.checkSaveChangesBtnState()
      }
    },
    async saveChanges() {
      if (!this.checkDeleteBtnState()) return
      let saveItems = utils.func.refreshJSON(this.filteredItems)
      saveItems = saveItems.map(f => {
        if (f.actionType && f.actionType.value) {
          f.actionType = f.actionType.value
        }
        delete f.validStartDate
        delete f.validEndDate
        delete f.validActivatingDueDate
        delete f.validPairingDueDate
        return f
      })
      let saveData = [{
        campaignID: this.campaignID,
        schedules: saveItems,
        numberOfDaysPerStage: this.getStageDays(saveItems)
      }]
      let formData = new FormData()
      formData.append('data', JSON.stringify(saveData));
      await this.$store.dispatch('campaignTimeline/saveTimeline', formData)
      if (this.savedTimeline) {
        this.currentMode = 'view'
        this.$swal({
          text: 'Successfully Saved.',
          type: 'success'
        })
        this.getCampaignTimelines()
        this.$emit('updated')
      }
    },
    // async saveScheduleLineUp() {
    //   console.log(this.filteredItems)
    //   let saveItems = utils.func.refreshJSON(this.filteredItems)
    //   saveItems = saveItems.map(f => {
    //     delete f.validStartDate
    //     delete f.validEndDate
    //     return f
    //   })
    //   let saveData = [{
    //     campaignID: this.campaignID,
    //     schedules: saveItems
    //   }]
    //   let formData = new FormData()
    //   formData.append('data', JSON.stringify(saveData));
    //   await this.$store.dispatch('campaignTimeline/saveTimeline', formData)
    // },
    downloadImage() {
      const vm = this
      this.isLoading = true
      this.hideForPrint = true
      var node = document.getElementById('img-node');
      htmlToImage.toPng(node)
        .then(function (dataUrl) {
          vm.isLoading = false
          vm.hideForPrint = false
          utils.func.downloadURI(dataUrl, `${vm.selectedCampaign ? vm.selectedCampaign.name : 'campaign-timeline'}.png`)
        })
        .catch(function (error) {
          vm.isLoading = false
          vm.hideForPrint = false
          console.error('oops, something went wrong!', error);
        });
    },
    populateDefaultItems() {
      this.filteredItems = Object.assign([], timelineConfig.defaultTableItems)
      this.filteredItems = this.resetDateValidators(this.filteredItems)
    },
    resetDateValidators(items) {
      items = items.map(d => {
        d.validStartDate = true
        d.validEndDate = true
        d.validActivatingDueDate = true
        d.validPairingDueDate = true
        return d
      })
      // items = this.reorderScheduleItems(items)
      return items
    },
    // reorderScheduleItems(items) {
    //   items = items.map(d => {
    //     if (!d.createdAt) {
    //       d.createdAt = moment().toISOString()
    //     }
    //     return d
    //   })
    //   return items
    // },
    formatDesc(text) {
      return text.replaceAll('\n', '</br>')
    },
    setDateFromPicker(item, fieldName, value) {
      const itemIdx = this.filteredItems.indexOf(item)
      this.filteredItems[itemIdx][fieldName] = utils.func.formatCustomDate(value, 'DD/MM/YYYY')
      this.filteredItems = utils.func.refreshJSON(this.filteredItems)
      this.checkSaveChangesBtnState()
    },
    checkDate(item, fieldName, validFieldName) {
      // check date format if entered only
      item[validFieldName] = false
      const itemIdx = this.filteredItems.indexOf(item)
      if (utils.func.isDateFormatValid(item[fieldName]) || !item[fieldName]) {
        item[validFieldName] = true
      }
      this.filteredItems[itemIdx] = item
      this.filteredItems = utils.func.refreshJSON(this.filteredItems)
      this.checkSaveChangesBtnState()
    },
    checkSaveChangesBtnState() {
      // if not empty state, check save changes for filling both start date and end date
      this.disableSave = false
      this.filteredItems.map(f => {
        if (f.actionType !== this.timelineTypes.EMPTY) {
          if (!f.validStartDate || !f.validEndDate || (!f.startDate && !f.endDate) ||
          ((f.actionType === this.timelineTypes.ACTIVATING || f.actionType === this.timelineTypes.LINEUP) && (!f.validActivatingDueDate || !f.validPairingDueDate))) {
            // for activating, line up stage, check if activating or pairing due date field is entered
            this.disableSave = true
          }
        }
      })
    },
    addAction() {
      this.filteredItems.push({
        timelineDate: '',
        startDate: '',
        endDate: '',
        actionType: this.timelineTypeLists.find(t => t.value === this.timelineTypes.ACTIVATING),
        actionDesc: '-',
        actionBy: '',
        validStartDate: true,
        validEndDate: true,
        createdAt: moment().toISOString()
      })
      this.checkSaveChangesBtnState()
    },
    displayActionType(actionType) {
      if (actionType.value) {
        const matchType = this.timelineTypeLists.find(t => t.value === actionType.value)
        if (matchType) return matchType.title
      }
      if (actionType) {
        return actionType
      }
      return ''
    },
    deleteItem(item) {
      if (!this.checkDeleteBtnState()) return
      this.filteredItems.splice(this.filteredItems.indexOf(item), 1)
      this.checkSaveChangesBtnState()
    },
    invokeReviewMeetingUpdated() {
      this.$emit('updated')
    },
    handleDataTableDragDrop(items) {
      // check and save drag n drop update for table items
      if (this.viewMode) return
      this.filteredItems = Object.assign([], items)
    },
    changeActionType() {
      this.checkSaveChangesBtnState()
    },
    checkDeleteBtnState() {
      // check for 1 count for at least four major stages to allow deleting stages
      let missingStages = []
      let validFourStages = this.timelineTypeLists.filter(t => t.value !== this.timelineTypes.EMPTY)
      validFourStages = validFourStages.map(v => { v.count = 0; return v })
      this.filteredItems.map(f => {
        const idx = validFourStages.findIndex(v => f.actionType && v.value === f.actionType.value)
        if (idx !== -1) {
          validFourStages[idx].count = 1
        }
      })
      validFourStages.map(v => {
        if (!v.count) missingStages.push(v.title)
      })
      if (missingStages.length) {
        let notifyText = ''
        if (missingStages.length > 1) {
          notifyText = `"${missingStages.join(', ')}" stage(s) are missing.`
        } else {
          notifyText = `"${missingStages.join(', ')}" stage is missing.`
        }
        notifyText += ' Please complete all stages.'
        this.$swal({
          title: 'Error',
          text: notifyText,
          type: 'error'
        })
        return false
      }
      return true
    },
    showActivatingDueDateBox(item) {
      if (item.actionType &&
      item.actionType.value === this.timelineTypes.ACTIVATING &&
      item.startDate &&
      item.endDate
      ) {
        return true
      }
      return false
    },
    showPairingDueDateBox(item) {
      if (item.actionType &&
      item.actionType.value === this.timelineTypes.LINEUP &&
      item.startDate &&
      item.endDate
      ) {
        return true
      }
      return false
    }
  }
}
</script>
<style>
  .campaign-timeline-table {
    border: 1px solid #dddddd;
    border-bottom: 2px solid #dddddd;
  }
  .campaign-timeline-table tbody td {
    border-right: 1px solid #dddddd;
    border-left: 1px solid #dddddd;
    height: 50px!important;
  }
  .campaign-timeline-table .v-data-table__wrapper {
    overflow-x: hidden!important;
  }
  .campaign-timeline-table .v-data-table-header th {
    font-size: 15px!important;
  }
</style>
<style scoped>
  .edit-date-box {
    max-width: 150px;
  }
  .campaign-timeline-table .date-picker-inner {
    margin-left: -35px;
    padding-top: 3px;
  }
  .move-cursor {
    cursor: move;
    margin-left: -5px;
    padding-right: 10px;
  }
  
  .print-table-inner {
    justify-content:center;
    margin:0 auto;
  }
  /* .print-table-inner #img-node {
    opacity: 0;
  } */
  .print-table-inner.table-edit-mode {
    max-width: 1000px;
  }
  .print-table-inner.table-view-mode {
    max-width: 700px;
  }
</style>



// WEBPACK FOOTER //
// DataTable.vue?40959c50